export const formatLatLon = (lat, lon) => {
  lat = parseFloat(lat).toFixed(5);
  lon = parseFloat(lon).toFixed(5);
  lat = (lat < 0 ? "" : "+") + lat;
  lon = (lon < 0 ? "" : "+") + lon;
  return `${lat}, ${lon}`;
};

/**
 * Calculate the distance between two coordinates using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in kilometers
};

/**
 * Find the facility that contains the given coordinates within its radius
 * @param {number} latitude - Event latitude
 * @param {number} longitude - Event longitude
 * @param {Object} facilities - Object containing all facilities keyed by facilityId
 * @returns {Object|null} The matching facility object or null if no match
 */
export const findFacilityByRadius = (latitude, longitude, facilities) => {
  if (!latitude || !longitude || !facilities) return null;
  if (latitude === 0 && longitude === 0) return null;

  let closestFacility = null;
  let closestDistance = Infinity;

  Object.values(facilities).forEach((facility) => {
    const { location = {}, radius = 0 } = facility;
    const { latitude: facLat, longitude: facLon } = location;

    if (!facLat || !facLon || !radius) return;

    const distance = calculateDistance(latitude, longitude, facLat, facLon);
    
    // Check if within radius (radius is stored in km)
    if (distance <= radius && distance < closestDistance) {
      closestDistance = distance;
      closestFacility = facility;
    }
  });

  return closestFacility;
};

/**
 * Get facility name based on coordinates and radius matching
 * @param {number} latitude - Event latitude
 * @param {number} longitude - Event longitude
 * @param {Object} facilities - Object containing all facilities keyed by facilityId
 * @returns {string} Facility name or "-" if not within any facility radius
 */
export const getFacilityNameByRadius = (latitude, longitude, facilities) => {
  const facility = findFacilityByRadius(latitude, longitude, facilities);
  return facility ? facility.name : "-";
};
